<script setup lang="ts">
import { min, regex } from '@vee-validate/rules';
import { Form } from 'vee-validate';
import { computed, ref } from 'vue';

import { subscribeCustomer } from '@/api/contacts';
import kennyError from '@/assets/Poses_KennyUhOhError1-Color.png';
import CheckmarkIcon from '@/components/base/assets/CheckmarkIcon.vue';
import WarningIcon from '@/components/base/assets/WarningIcon.vue';
import FormInput from '@/components/base/form/FormInput.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import Caption from '@/components/base/typography/Caption.vue';
import Header3 from '@/components/base/typography/Header3.vue';
import LargeBodyText from '@/components/base/typography/LargeBodyText.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import { OnsiteOfferConfig } from '@/composables/personalization/useOnsiteOffer';
import { useCallback } from '@/composables/useCallback';
import { FlexibleMessageValidator, useForm } from '@/composables/useForm';
import { useCustomer } from '@/stores/customer';
import { sendButtonClicked } from '@/utils/analytics/buttonClicked';
import { sendContactSubscribed } from '@/utils/analytics/contactSubscribed';

const { analyticsEventName, emailInput, existingContact, secondaryScreen, smsSubscribed } =
  defineProps<{
    analyticsEventName: string;
    emailInput: string;
    existingContact: boolean;
    secondaryScreen: NonNullable<OnsiteOfferConfig['secondaryScreen']>;
    smsSubscribed: boolean;
  }>();

const emit = defineEmits<{
  'phone-signup-completed': [existingContact: boolean];
}>();

const backgroundImageDesktop = computed(() => `url('${secondaryScreen.mainImageDesktopSrc}')`);
const backgroundImageMobile = computed(() => `url('${secondaryScreen.mainImageMobileSrc}')`);
const phoneInput = ref<string>();

const customerStore = useCustomer();
const { errorMessages, validatorRegex, validatorFailed } = useForm();

const rules: Record<'phone', FlexibleMessageValidator<string>> = {
  phone: (value) => {
    if (!regex(value, [validatorRegex.phone])) return errorMessages.phone.regex;
    if (!min(value, [10])) return errorMessages.phone.min.replace('{length}', '10');
    return true;
  },
};

const onSubmit = useCallback(async () => {
  let response;

  sendButtonClicked({
    buttonName: 'FTB Second Screen - Submit',
    content: secondaryScreen.buttonCta,
    pageSection: 'FTB Popup',
  });

  if (phoneInput.value) {
    response = await subscribeCustomer({
      attentiveSignUpSourceId: existingContact
        ? secondaryScreen.attentiveSignUpSourceIdExisting
        : secondaryScreen.attentiveSignUpSourceIdNetNew,
      email: emailInput,
      phone: phoneInput.value,
      reason: analyticsEventName,
    });

    sendContactSubscribed({
      email: emailInput,
      phone: phoneInput.value,
      reason: analyticsEventName,
      source: analyticsEventName,
    });
  }

  if (response?.contact) {
    customerStore.contact = response.contact;
  }

  customerStore.sendIdentifyEvent();

  emit('phone-signup-completed', existingContact);
});
</script>

<template>
  <div class="flex flex-col justify-center items-center md:flex-row">
    <div v-if="smsSubscribed" class="grid flex-1 gap-4 px-6 py-6">
      <img class="max-w-20" :src="kennyError" alt="" />
      <Header3>Oh Nuts!</Header3>
      <LargeBodyText>
        Looks like we already have your email in our system. This offer is valid for new customers
        only.
      </LargeBodyText>
    </div>
    <div v-else class="flex-1 px-6 py-6">
      <div class="flex gap-1 items-center">
        <WarningIcon v-if="existingContact" class="text-nuts-red-800" :size="16" />
        <CheckmarkIcon v-else class="text-nuts-lime-800" :size="16" />
        <SmallBodyText>
          {{
            existingContact
              ? 'Looks like we already have your email in our system.'
              : 'Offer applied to your cart.'
          }}
        </SmallBodyText>
      </div>
      <Header3 class="mt-2">
        {{ existingContact ? secondaryScreen.headlineExisting : secondaryScreen.headlineNetNew }}
      </Header3>
      <LargeBodyText class="mt-4 font-bold">
        {{ secondaryScreen.caption }}
      </LargeBodyText>
      <div class="mt-4 w-full md:mt-7">
        <Caption class="text-neutral-500">
          By submitting this form, you agree to receive recurring automated marketing text messages
          (e.g. AI content, cart reminders) from Nuts.com at the number you provide. Consent not a
          condition of purchase. We may share info with service providers per our Privacy Policy.
          Reply HELP for help & STOP to cancel. Msg frequency varies. Msg & data rates may apply. By
          submitting this form, you also agree to our
          <a href="https://attnl.tv/t/7Lk/r/US" target="_blank">Terms</a> (incl. arbitration) &
          <a href="https://attnl.tv/p/7Lk" target="_blank">Privacy Policy</a>.
        </Caption>
        <Form v-slot="{ meta: formEmailMeta }" @submit="onSubmit.execute()" class="grid gap-4 mt-4">
          <FormInput
            v-model="phoneInput"
            class="w-full"
            :inputAttributes="{
              autocomplete: 'tel',
              name: 'phone',
              placeholder: 'Phone Number (Optional)',
              type: 'tel',
            }"
            showPlaceholder
            theme="dark"
            :validator="rules.phone"
          />
          <ThemedButton
            class="w-full h-12"
            :disabled="(formEmailMeta.touched && validatorFailed(formEmailMeta)) ?? false"
            :isLoading="onSubmit.isPending"
            size="default"
            theme="dark-yellow"
            type="submit"
          >
            {{ secondaryScreen.buttonCta }}
          </ThemedButton>
        </Form>
        <BaseBodyText v-if="onSubmit.error" class="mt-4 text-red-500">
          {{ onSubmit.error }}
        </BaseBodyText>
      </div>
    </div>

    <div class="flex-1 w-full h-full bg-center bg-no-repeat bg-cover dynamic-bg" />
  </div>
</template>

<style scoped>
.dynamic-bg {
  background-image: v-bind(backgroundImageMobile);
}
@media (min-width: 768px) {
  .dynamic-bg {
    background-image: v-bind(backgroundImageDesktop);
  }
}
</style>
